// hooks/useExpandAllRows.js
//
// The grid side of the "Expand all" / "Collapse all" link
// (components/grid/ExpandAllButton.jsx): a page spreads `gridProps` on its
// <AgGridReact> and the rest on <ExpandAllButton>. The hook reads the grid
// through its own events — a row opened or closed, expand all / collapse all,
// and a model update (a page of rows arriving) — so it needs no grid api
// before the grid is ready. "All" is every row the grid has loaded that can
// open (AG Grid's expandAll walks the loaded nodes; no grid here sets
// `ssrmExpandAllAffectsAllRows`), not rows the server has not sent yet.

import { useCallback, useMemo, useRef, useState } from 'react';

export function useExpandAllRows() {
  const apiRef = useRef(null);
  // `enabled`: at least one loaded row can open. `allExpanded`: every such
  // row is open, so the button offers to collapse.
  const [{ enabled, allExpanded }, setExpansion] = useState({ enabled: false, allExpanded: false });

  const recompute = useCallback((api) => {
    apiRef.current = api;
    let openable = 0;
    let open = 0;
    api.forEachNode((node) => {
      if (!node.master && !node.group) return;
      openable += 1;
      if (node.expanded) open += 1;
    });
    setExpansion({ enabled: openable > 0, allExpanded: openable > 0 && open === openable });
  }, []);

  const gridProps = useMemo(() => ({
    onModelUpdated: (event) => recompute(event.api),
    onRowGroupOpened: (event) => recompute(event.api),
    onExpandOrCollapseAll: (event) => recompute(event.api),
  }), [recompute]);

  const toggle = useCallback(() => {
    const api = apiRef.current;
    if (allExpanded) api.collapseAll();
    else api.expandAll();
  }, [allExpanded]);

  return { gridProps, enabled, allExpanded, toggle };
}
